import type { Components, Theme } from '@mui/material/styles'

import { INPUT_BORDER_RADIUS, MULTILINE_TEXT_MOBILE_BORDER_RADIUS } from './layoutConstants'

const DATE_PICKER_BORDER_READIUS = '15px'

const withTheme =
  <T extends object>(styles: (theme: Theme) => T) =>
  ({ theme }: { theme: unknown }) =>
    styles(theme as Theme)

export const createComponentOverrides = (): Components => ({
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: INPUT_BORDER_RADIUS,
        fontSize: 20,
        fontWeight: 400,
        padding: '8px 30px',
        borderWidth: 2,
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: withTheme((theme) => ({
        fontSize: 18,
        lineHeight: '24px',
        left: 15,
        [theme.breakpoints.down('tablet')]: { fontSize: 15, lineHeight: '33px' },
      })),
      shrink: {
        left: 1,
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: withTheme((theme) => ({
        borderRadius: INPUT_BORDER_RADIUS,
        backgroundColor: theme.palette.background.light,
        fontSize: 18,
        lineHeight: '24px',
        '&.MuiInputBase-multiline': {
          borderRadius: MULTILINE_TEXT_MOBILE_BORDER_RADIUS,
        },
        '& .MuiSelect-select': {
          height: '1em !important',
        },
        [theme.breakpoints.down('tablet')]: { fontSize: 15, lineHeight: '33px' },
      })),
      input: {
        padding: '15px 30px',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: withTheme((theme) => ({
        fontSize: 18,
        [theme.breakpoints.down('tablet')]: { fontSize: 15 },
      })),
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      label: withTheme((theme) => ({
        fontSize: 18,
        [theme.breakpoints.down('tablet')]: { fontSize: 15 },
      })),
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: withTheme((theme) => ({
        fontSize: 15,
        [theme.breakpoints.down('tablet')]: { fontSize: 12 },
      })),
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: withTheme((theme) => ({
        color: theme.palette.secondary.contrastText,
      })),
      popper: {
        position: 'fixed',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: withTheme((theme) => ({
        fontSize: 18,
        [theme.breakpoints.down('tablet')]: { fontSize: 15 },
      })),
    },
  },
  MuiList: {
    styleOverrides: {
      root: withTheme((theme) => ({
        fontSize: 18,
        [theme.breakpoints.down('tablet')]: { fontSize: 15 },
      })),
    },
  },
  MuiListItemText: {
    styleOverrides: {
      primary: withTheme((theme) => ({
        fontSize: 18,
        [theme.breakpoints.down('tablet')]: { fontSize: 15 },
      })),
    },
  },
  MuiPickersLayout: {
    styleOverrides: {
      root: {
        backgroundColor: 'transparent !important',
      },
    },
  },
  MuiDateCalendar: {
    styleOverrides: {
      root: withTheme((theme) => ({
        width: 350,
        borderRadius: DATE_PICKER_BORDER_READIUS,
        backgroundColor: theme.palette.primary.contrastText,
        fontSize: 18,
      })),
    },
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      label: withTheme((theme) => ({
        fontSize: 18,
        [theme.breakpoints.down('tablet')]: { fontSize: 15 },
      })),
    },
  },
  MuiDayCalendar: {
    styleOverrides: {
      weekContainer: withTheme((theme) => ({
        fontSize: 18,
        [theme.breakpoints.down('tablet')]: { fontSize: 15 },
      })),
    },
  },
  MuiPickersMonth: {
    styleOverrides: {
      monthButton: withTheme((theme) => ({
        fontSize: 18,
        [theme.breakpoints.down('tablet')]: { fontSize: 15 },
        '&.Mui-selected': {
          backgroundColor: `${theme.palette.primary.main} !important`,
          color: 'white',
          '&:hover': {
            backgroundColor: `${theme.palette.primary.dark} !important`,
          },
        },
      })),
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      root: withTheme((theme) => ({
        '&.Mui-selected': {
          backgroundColor: `${theme.palette.primary.main} !important`,
          color: 'white',
          '&:hover': {
            backgroundColor: `${theme.palette.primary.dark} !important`,
          },
        },
      })),
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: '20px',
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: withTheme((theme) => ({
        borderBottom: `1px solid ${theme.palette.text.greyOut}`,
        padding: '20px 43px',
        fontSize: 22,
        [theme.breakpoints.down('tablet')]: { fontSize: 17 },
      })),
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: withTheme((theme) => ({
        padding: '20px 43px',
        fontSize: 18,
        [theme.breakpoints.down('tablet')]: { fontSize: 15 },
      })),
    },
  },
})
